import type { CommerceOperation } from "../contracts/commerce-operation.js";
import type { CommerceTransport } from "../contracts/commerce-transport.js";
import type { CommerceTransportContext } from "../contracts/commerce-transport-context.js";
import { CommerceTransportError } from "../errors/commerce-transport-error.js";

export interface HttpCommerceTransportOptions {
    readonly baseUrl: string;
    readonly fetch?: typeof globalThis.fetch;
    readonly headers?: Readonly<Record<string, string>>;
}

interface CommerceGatewayErrorResponse {
    readonly code?: string;
    readonly message?: string;
    readonly operation?: string | null;
    readonly correlationId?: string | null;
}

export class HttpCommerceTransport implements CommerceTransport {
    public readonly name = "Teqtank Commerce Gateway HTTP Transport";
    public readonly sessionPersistenceScope: string;

    private readonly baseUrl: string;
    private readonly fetchImplementation: typeof globalThis.fetch;
    private readonly defaultHeaders: Readonly<Record<string, string>>;

    public constructor(options: HttpCommerceTransportOptions) {
        this.baseUrl = normalizeBaseUrl(options.baseUrl);
        this.sessionPersistenceScope = this.baseUrl;
        this.fetchImplementation =
            options.fetch ?? ((input, init) => globalThis.fetch(input, init));
        this.defaultHeaders = Object.freeze({ ...(options.headers ?? {}) });

        if (typeof this.fetchImplementation !== "function") {
            throw new TypeError("HttpCommerceTransport requires a fetch implementation.");
        }
    }

    public async execute<TRequest, TResponse>(
        operation: CommerceOperation<TRequest, TResponse>,
        request: TRequest,
        context: CommerceTransportContext = {},
    ): Promise<TResponse> {
        const url = `${this.baseUrl}/api/commerce/operations/${encodeURIComponent(operation.name)}`;
        const headers = this.createHeaders(context);

        let response: Response;

        try {
            response = await this.fetchImplementation(url, {
                method: "POST",
                credentials: "include",
                headers,
                body: JSON.stringify(request),
                ...(context.signal === undefined ? {} : { signal: context.signal }),
            });
        } catch (cause) {
            console.error(
                `Commerce Gateway request failed for operation '${operation.name}':`,
                cause,
            );
            if (context.signal?.aborted === true || isAbortError(cause)) {
                throw new CommerceTransportError(
                    "request-cancelled",
                    `Commerce operation '${operation.name}' was cancelled.`,
                    {
                        operationName: operation.name,
                        cause,
                    },
                );
            }

            throw new CommerceTransportError(
                "request-failed",
                `Commerce Gateway request failed for operation '${operation.name}'.`,
                {
                    operationName: operation.name,
                    cause,
                },
            );
        }

        const responseText = await response.text();
        const responseBody = parseJson(responseText);

        if (!response.ok) {
            const gatewayError = asGatewayError(responseBody);
            const message =
                gatewayError?.message?.trim() ||
                `Commerce Gateway returned HTTP ${response.status} for operation '${operation.name}'.`;

            throw new CommerceTransportError(
                response.status === 501 ? "operation-unsupported" : "request-failed",
                message,
                {
                    operationName: operation.name,
                    cause: {
                        status: response.status,
                        statusText: response.statusText,
                        gatewayError,
                        correlationId: response.headers.get("X-Correlation-ID"),
                    },
                },
            );
        }

        if (responseText.trim().length === 0) {
            throw new CommerceTransportError(
                "response-invalid",
                `Commerce Gateway returned an empty response for operation '${operation.name}'.`,
                {
                    operationName: operation.name,
                },
            );
        }

        if (responseBody === undefined) {
            throw new CommerceTransportError(
                "response-invalid",
                `Commerce Gateway returned invalid JSON for operation '${operation.name}'.`,
                {
                    operationName: operation.name,
                },
            );
        }

        return responseBody as TResponse;
    }

    public async clearSessionPersistence(context: CommerceTransportContext): Promise<void> {
        const response = await this.fetchImplementation(`${this.baseUrl}/api/commerce/session`, {
            method: "DELETE",
            credentials: "include",
            headers: this.createHeaders(context),
            ...(context.signal === undefined ? {} : { signal: context.signal }),
        });

        if (!response.ok) {
            throw new CommerceTransportError(
                "request-failed",
                "The persisted Commerce session could not be cleared.",
                {
                    operationName: "commerce.session.clear-persistence",
                    cause: { status: response.status, statusText: response.statusText },
                },
            );
        }
    }

    private createHeaders(context: CommerceTransportContext): Headers {
        const headers = new Headers(this.defaultHeaders);

        headers.set("Accept", "application/json");
        headers.set("Content-Type", "application/json");
        setOptionalHeader(headers, "X-Correlation-ID", context.correlationId);
        setOptionalHeader(headers, "X-Teqtank-Commerce-Session", context.sessionToken);
        setOptionalHeader(headers, "X-Teqtank-Commerce-Key", context.commerceKey);
        setOptionalHeader(headers, "X-Teqtank-Commerce-Data-Source", context.dataSource);
        setOptionalHeader(headers, "X-Teqtank-Commerce-Tenant", context.tenantId);
        setOptionalHeader(headers, "X-Teqtank-Commerce-Market", context.marketId);
        setOptionalHeader(headers, "X-Teqtank-Commerce-Currency", context.currencyCode);
        setOptionalHeader(headers, "X-Teqtank-Commerce-Locale", context.locale);

        return headers;
    }
}

function normalizeBaseUrl(value: string): string {
    if (typeof value !== "string" || value.trim().length === 0) {
        throw new TypeError("HttpCommerceTransport baseUrl is required.");
    }

    let url: URL;

    try {
        url = new URL(value.trim());
    } catch (cause) {
        throw new TypeError(
            "HttpCommerceTransport baseUrl must be an absolute HTTP or HTTPS URL.",
            { cause },
        );
    }

    if (url.protocol !== "http:" && url.protocol !== "https:") {
        throw new TypeError("HttpCommerceTransport baseUrl must use HTTP or HTTPS.");
    }

    return url.toString().replace(/\/$/u, "");
}

function normalizeOptionalValue(value: string | undefined): string | undefined {
    if (value === undefined) {
        return undefined;
    }

    const normalized = value.trim();

    return normalized.length === 0 ? undefined : normalized;
}

function setOptionalHeader(headers: Headers, name: string, value: string | undefined): void {
    const normalized = normalizeOptionalValue(value);

    if (normalized !== undefined) {
        headers.set(name, normalized);
    }
}

function parseJson(value: string): unknown | undefined {
    if (value.trim().length === 0) {
        return undefined;
    }

    try {
        return JSON.parse(value) as unknown;
    } catch {
        return undefined;
    }
}

function asGatewayError(value: unknown): CommerceGatewayErrorResponse | undefined {
    if (typeof value !== "object" || value === null) {
        return undefined;
    }

    return value as CommerceGatewayErrorResponse;
}

function isAbortError(value: unknown): boolean {
    return value instanceof DOMException && value.name === "AbortError";
}
